import { VISIBILITY_LABELS } from '@/js/core/constants/tray-constants';
import {
  DEFAULT_TIMEZONE,
  getTimezoneOffsetMs,
  getZonedParts,
  normalizeTimezone,
} from '@/js/core/utils/timezone';

const MYSQL_DATETIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/;
const EXPLICIT_TIMEZONE_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/i;

function wallTimeToDate(parts, timezone) {
  const utcGuess = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  const firstOffset = getTimezoneOffsetMs(new Date(utcGuess), timezone);
  const firstDate = new Date(utcGuess - firstOffset);
  const secondOffset = getTimezoneOffsetMs(firstDate, timezone);

  if (firstOffset !== secondOffset) {
    return new Date(utcGuess - secondOffset);
  }

  return firstDate;
}

export function parseDateTime(dateString, sourceTimezone = DEFAULT_TIMEZONE) {
  if (!dateString) return null;

  if (dateString instanceof Date) {
    return Number.isNaN(dateString.getTime()) ? null : dateString;
  }

  if (typeof dateString === 'number') {
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof dateString !== 'string') return null;

  const value = dateString.trim();
  if (!value) return null;

  if (EXPLICIT_TIMEZONE_PATTERN.test(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const match = value.match(MYSQL_DATETIME_PATTERN);
  if (match) {
    return wallTimeToDate(
      {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
        hour: Number(match[4] || 0),
        minute: Number(match[5] || 0),
        second: Number(match[6] || 0),
      },
      normalizeTimezone(sourceTimezone)
    );
  }

  const date = new Date(value.replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateKey(
  dateString,
  timezone = DEFAULT_TIMEZONE,
  sourceTimezone = DEFAULT_TIMEZONE
) {
  const date = parseDateTime(dateString, sourceTimezone);
  if (!date) return '';

  const parts = getZonedParts(date, timezone);

  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function getZonedDateTimeInfo(
  dateString,
  timezone = DEFAULT_TIMEZONE,
  sourceTimezone = DEFAULT_TIMEZONE
) {
  const date = parseDateTime(dateString, sourceTimezone);

  if (!date) return null;

  const parts = getZonedParts(date, timezone, { weekday: 'short' });
  const displayParts = getZonedParts(date, timezone, { month: 'short' });
  const hours24 = Number(parts.hour);
  const minutes = Number(parts.minute);
  const hours = hours24 % 12 || 12;

  return {
    iso: date.toISOString(),
    dateKey: `${parts.year}-${parts.month}-${parts.day}`,
    dayName: parts.weekday,
    monthName: displayParts.month,
    dayNumber: Number(parts.day),
    year: Number(parts.year),
    hours,
    hasMinutes: minutes > 0,
    minutes: minutes.toString().padStart(2, '0'),
    ampm: hours24 >= 12 ? 'pm' : 'am',
  };
}

export function isDateBeforeToday(
  dateString,
  timezone = DEFAULT_TIMEZONE,
  sourceTimezone = DEFAULT_TIMEZONE
) {
  const dateKey = formatDateKey(dateString, timezone, sourceTimezone);
  const todayKey = formatDateKey(new Date(), timezone);

  return Boolean(dateKey && todayKey && dateKey < todayKey);
}

/**
 * Convert sanitized HTML-ish text into plain text for labels and slug sources.
 */
export function plainTextFromHtml(value) {
  const text = String(value || '').replace(/<[^>]*>/g, '');

  if (typeof document === 'undefined') {
    return text;
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;

  return textarea.value;
}

/**
 * Format a date with day of week and time (e.g., "Mon, Jan 15, 2024, 3:45pm")
 * @param {string} dateString - ISO date string
 * @param {string} timezone - IANA timezone ID
 * @returns {string} Formatted date with time
 */
export function formatCommentDate(dateString, timezone = DEFAULT_TIMEZONE) {
  if (!dateString) return '';
  const date = parseDateTime(dateString);
  if (!date) return '';

  const datePart = date.toLocaleDateString('en-US', {
    timeZone: normalizeTimezone(timezone),
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const timePart = date
    .toLocaleTimeString('en-US', {
      timeZone: normalizeTimezone(timezone),
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();
  return `${datePart}, ${timePart}`;
}

/**
 * Format a visibility value to a human-readable label
 * @param {string} visibility - Visibility value (everyone, members, me)
 * @returns {string} Human-readable label
 */
export function formatVisibility(visibility) {
  return VISIBILITY_LABELS[visibility] || visibility;
}

/**
 * Make long strings breakable by inserting <wbr> tags. Does not break inside HTML tags.
 *
 * @param {string} title - Title text (may contain HTML)
 * @returns {string} Title with <wbr> inserted after each forward/back slash
 */
export function wrapBreakableTitle(title) {
  return String(title || '').replace(/<[^>]*>|([/\\])/g, (match, slash) =>
    slash ? `${slash}<wbr>` : match
  );
}

/**
 * Truncate text content to a maximum length
 * @param {string} content - Content to truncate (can contain HTML)
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated plain text
 */
export function truncateContent(content, maxLength = 100) {
  if (!content) return '';
  const stripped = plainTextFromHtml(content);
  if (stripped.length <= maxLength) return stripped;
  return stripped.substring(0, maxLength) + '...';
}
